import React, { useState, useMemo, useContext, useEffect } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcSync, IcCog, IcPuzzle, IcTag, IcFileInvoiceDollar, IcCheckCircle, IcHourglass, IcSearch, IcChartPie, IcTable, IcChartBar } from "../../Icons";
import axios from "axios";
import Header from "../../layout/Header/Header";
import { Button, Input, Label, Spinner } from "reactstrap";
import moment from "moment";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList, Legend,
} from "recharts";
// ── Configuración de categorías ──────────────────────────────────────────────
const CATS = ["Mensualidad", "Setup", "Complemento", "Otros"];

const CAT_CFG = {
  Mensualidad: { color: "#10b981", light: "#d1fae5", dark: "#065f46", icon: <IcSync />    },
  Setup:       { color: "#3b82f6", light: "#dbeafe", dark: "#1e3a8a", icon: <IcCog />     },
  Complemento: { color: "#f59e0b", light: "#fef3c7", dark: "#78350f", icon: <IcPuzzle />  },
  Otros:       { color: "#94a3b8", light: "#f1f5f9", dark: "#334155", icon: <IcTag />     },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt  = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const pct  = (n, d) => (d && d > 0 ? ((n / d) * 100).toFixed(1) : "0.0");
const fmtK = (v)   => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`;


// ── Custom shape: esquinas superiores redondeadas (para stacked bars) ────────
const TopRoundedBar = ({ x, y, width, height, fill }) => {
  if (!height || height <= 0) return null;
  const r = Math.min(5, height);
  return (
    <path
      fill={fill}
      d={`M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} Z`}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
function ReporteCargos() {
  const { user }    = useContext(AuthContext);
  const URL_CARGOS  = process.env.REACT_APP_URL_CARGOS;

  const hoy = new Date();
  const [fechaInicio, setFechaInicio] = useState("2025-01-01");
  const [fechaFin,    setFechaFin]    = useState(moment(hoy).format("YYYY-MM-DD"));
  const [cargos,      setCargos]      = useState([]);
  const [loading,     setLoading]     = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = () => {
    setLoading(true);
    axios
      .get(`${URL_CARGOS}/fechas/${fechaInicio}/${fechaFin}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((res) => { setCargos(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  // ── Stats por categoría ────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const totalImporte = cargos.reduce((s, c) => s + (c.importe || 0), 0);
    const totalCobrado = cargos.reduce((s, c) => s + (c.abonos  || 0), 0);
    const totalSaldo   = cargos.reduce((s, c) => s + (c.saldo   || 0), 0);
    const total        = cargos.length;

    const bycat = CATS.map((cat) => {
      const rows = cargos.filter((c) => (c.categoria || "Otros") === cat);
      const imp  = rows.reduce((s, c) => s + (c.importe || 0), 0);
      const abo  = rows.reduce((s, c) => s + (c.abonos  || 0), 0);
      const sal  = rows.reduce((s, c) => s + (c.saldo   || 0), 0);
      return {
        name:    cat,
        count:   rows.length,
        importe: imp,
        abonos:  abo,
        saldo:   sal,
        pctImp:  pct(imp, totalImporte),
        pctCnt:  pct(rows.length, total),
        cobPct:  imp > 0 ? parseFloat(pct(abo, imp)) : 0,
      };
    });

    return { totalImporte, totalCobrado, totalSaldo, bycat, total };
  }, [cargos]);

  // ── Datos mensuales por categoría ─────────────────────────────────────────
  const monthlyData = useMemo(() => {
    if (!cargos.length) return [];
    const map = {};
    cargos.forEach((c) => {
      const d = c.fecha || c.createdAt;
      if (!d) return;
      const key = moment(d).format("YYYY-MM");
      if (!map[key]) map[key] = { mes: moment(d).format("MMM YY"), Setup: 0, Mensualidad: 0, Complemento: 0, Otros: 0 };
      const cat = c.categoria || "Otros";
      if (map[key][cat] !== undefined) map[key][cat] += (c.importe || 0);
      else map[key].Otros += (c.importe || 0);
    });
    return Object.keys(map).sort().map((k) => ({
      ...map[k],
      total: (map[k].Mensualidad || 0) + (map[k].Setup || 0) + (map[k].Complemento || 0) + (map[k].Otros || 0),
    }));
  }, [cargos]);



  if (!user.usuarios_permisos) return null;

  // ── Colección global ───────────────────────────────────────────────────────
  const cobPctGlobal = stats.totalImporte > 0
    ? parseFloat(pct(stats.totalCobrado, stats.totalImporte))
    : 0;

  return (
    <>
      <Header />
      <br />
      <div className="card container" style={{ maxWidth: 1140, padding: "0 16px 32px" }}>

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <IcChartPie style={{ marginRight: 8, color: "#3b82f6" }} />
              Reporte de Cargos
            </h3>
            <p className="page-subtitle">Distribución, tendencias y cobranza por categoría</p>
          </div>
          <div className="page-actions">
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div>
                <Label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b", marginBottom: 3, display: "block" }}>INICIO</Label>
                <Input bsSize="sm" type="date" value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)} style={{ minWidth: 130 }} />
              </div>
              <div>
                <Label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b", marginBottom: 3, display: "block" }}>FIN</Label>
                <Input bsSize="sm" type="date" value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)} style={{ minWidth: 130 }} />
              </div>
              <Button size="sm" color="primary" onClick={fetchData} style={{ height: 31 }}>
                <IcSearch style={{ marginRight: 5 }} />
                Consultar
              </Button>
            </div>
          </div>
        </div>

        {/* ── Loading ──────────────────────────────────────────────────────── */}
        {loading && (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b" }}>
            <Spinner style={{ color: "#3b82f6", width: "2.5rem", height: "2.5rem" }} />
            <div style={{ marginTop: 14, fontWeight: 600 }}>Cargando datos…</div>
          </div>
        )}

        {!loading && cargos.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <IcChartPie size={48} style={{ opacity: 0.3 }} />
            <div style={{ marginTop: 12, fontWeight: 600 }}>Sin datos en el período seleccionado</div>
          </div>
        )}

        {!loading && cargos.length > 0 && (
          <>
            {/* ── Globales ───────────────────────────────────────────────── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 22 }}>
              {[
                {
                  label: "Total Facturado",
                  value: fmt(stats.totalImporte),
                  sub: `${stats.total} cargos`,
                  icon: <IcFileInvoiceDollar />,
                  color: "#3b82f6",
                  bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                },
                {
                  label: "Total Cobrado",
                  value: fmt(stats.totalCobrado),
                  sub: `${cobPctGlobal.toFixed(1)}% de cobranza`,
                  icon: <IcCheckCircle />,
                  color: "#10b981",
                  bg: "linear-gradient(135deg, #f0fdf4 0%, #d1fae5 100%)",
                },
                {
                  label: "Saldo Pendiente",
                  value: fmt(stats.totalSaldo),
                  sub: `${stats.totalImporte > 0 ? (100 - cobPctGlobal).toFixed(1) : "0.0"}% por cobrar`,
                  icon: <IcHourglass />,
                  color: "#f59e0b",
                  bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                },
              ].map((g) => (
                <div key={g.label} style={{
                  background: g.bg,
                  border: `1px solid ${g.color}30`,
                  borderRadius: 14,
                  padding: "18px 20px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}>
                  <div>
                    <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.07em" }}>{g.label}</div>
                    <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#0f172a",
                      marginTop: 4, lineHeight: 1.1 }}>{g.value}</div>
                    <div style={{ fontSize: "0.72rem", color: g.color, fontWeight: 600, marginTop: 4 }}>{g.sub}</div>
                  </div>
                  <div style={{
                    width: 52, height: 52, borderRadius: "50%",
                    background: `${g.color}18`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: g.color,
                  }}>
                    {g.icon}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Barra de cobranza global ────────────────────────────────── */}
            <div style={{ marginBottom: 28, background: "#fff", border: "1px solid #e2e8f0",
              borderRadius: 12, padding: "14px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Eficiencia de Cobranza Global
                </span>
                <span style={{ fontSize: "1rem", fontWeight: 800, color: cobPctGlobal >= 80 ? "#10b981" : cobPctGlobal >= 50 ? "#f59e0b" : "#ef4444" }}>
                  {cobPctGlobal.toFixed(1)}%
                </span>
              </div>
              <div style={{ height: 10, background: "#f1f5f9", borderRadius: 10, overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: `${Math.min(cobPctGlobal, 100)}%`,
                  borderRadius: 10,
                  background: cobPctGlobal >= 80
                    ? "linear-gradient(90deg, #10b981, #34d399)"
                    : cobPctGlobal >= 50
                    ? "linear-gradient(90deg, #f59e0b, #fbbf24)"
                    : "linear-gradient(90deg, #ef4444, #f87171)",
                  transition: "width 0.8s ease",
                }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5,
                fontSize: "0.68rem", color: "#94a3b8" }}>
                <span>{fmt(stats.totalCobrado)} cobrado</span>
                <span>{fmt(stats.totalSaldo)} pendiente</span>
              </div>
            </div>

            {/* ── KPI Cards por categoría ─────────────────────────────────── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 28 }}>
              {stats.bycat.map((s) => {
                const cfg = CAT_CFG[s.name];
                return (
                  <div key={s.name} style={{
                    background: "#fff",
                    borderRadius: 14,
                    border: "1px solid #e2e8f0",
                    borderTop: `4px solid ${cfg.color}`,
                    padding: "16px 18px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    transition: "box-shadow 0.2s",
                  }}>
                    {/* Header de la card */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: cfg.light,
                          display: "flex", alignItems: "center", justifyContent: "center", color: cfg.color }}>
                          {cfg.icon}
                        </div>
                        <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569",
                          textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.name}</span>
                      </div>
                      <span style={{
                        background: cfg.light,
                        color: cfg.color,
                        borderRadius: 20,
                        padding: "2px 9px",
                        fontSize: "0.72rem",
                        fontWeight: 800,
                      }}>
                        {s.pctImp}%
                      </span>
                    </div>

                    {/* Importe */}
                    <div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", marginBottom: 2 }}>
                      {fmt(s.importe)}
                    </div>
                    <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginBottom: 12 }}>
                      {s.count} cargo{s.count !== 1 ? "s" : ""}
                    </div>

                    {/* Separador */}
                    <div style={{ height: 1, background: "#f1f5f9", marginBottom: 10 }} />

                    {/* Cobrado / Pendiente */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: "0.62rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Cobrado</div>
                        <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#10b981" }}>{fmt(s.abonos)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "0.62rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Pendiente</div>
                        <div style={{ fontSize: "0.8rem", fontWeight: 700, color: s.saldo > 0 ? "#f59e0b" : "#94a3b8" }}>{fmt(s.saldo)}</div>
                      </div>
                    </div>

                    {/* Mini barra de cobranza */}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.65rem",
                      color: "#64748b", marginBottom: 4 }}>
                      <span>Cobranza</span>
                      <span style={{ fontWeight: 700, color: cfg.color }}>{s.cobPct.toFixed(1)}%</span>
                    </div>
                    <div style={{ height: 6, background: "#f1f5f9", borderRadius: 10, overflow: "hidden" }}>
                      <div style={{
                        height: "100%",
                        width: `${Math.min(s.cobPct, 100)}%`,
                        background: cfg.color,
                        borderRadius: 10,
                        transition: "width 0.8s ease",
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Tabla de detalle ────────────────────────────────────────── */}
            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0",
              padding: "20px 20px 8px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", marginBottom: 12 }}>
              <div style={{ fontSize: "0.8rem", fontWeight: 800, color: "#0f172a", marginBottom: 18 }}>
                <IcTable style={{ marginRight: 8, color: "#3b82f6" }} />
                Detalle por Categoría
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                <thead>
                  <tr>
                    {[
                      { h: "Categoría",  align: "left"  },
                      { h: "Cargos",     align: "right" },
                      { h: "Importe",    align: "right" },
                      { h: "Cobrado",    align: "right" },
                      { h: "Pendiente",  align: "right" },
                      { h: "Cobranza",   align: "center" },
                      { h: "% del Total", align: "left" },
                    ].map(({ h, align }) => (
                      <th key={h} style={{
                        padding: "8px 12px",
                        textAlign: align,
                        color: "#64748b",
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        borderBottom: "2px solid #f1f5f9",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stats.bycat.map((s, i) => {
                    const cfg = CAT_CFG[s.name];
                    return (
                      <tr key={s.name} style={{
                        borderBottom: i < stats.bycat.length - 1 ? "1px solid #f8fafc" : "none",
                        transition: "background 0.15s",
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.background = cfg.light}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        {/* Categoría */}
                        <td style={{ padding: "14px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: cfg.light,
                              display: "flex", alignItems: "center", justifyContent: "center", color: cfg.color }}>
                              {cfg.icon}
                            </div>
                            <span style={{ fontWeight: 700, color: "#0f172a" }}>{s.name}</span>
                          </div>
                        </td>
                        {/* Cargos */}
                        <td style={{ padding: "14px 12px", textAlign: "right", color: "#475569", fontWeight: 600 }}>
                          {s.count}
                        </td>
                        {/* Importe */}
                        <td style={{ padding: "14px 12px", textAlign: "right", fontWeight: 700, color: "#0f172a" }}>
                          {fmt(s.importe)}
                        </td>
                        {/* Cobrado */}
                        <td style={{ padding: "14px 12px", textAlign: "right", fontWeight: 600, color: "#10b981" }}>
                          {fmt(s.abonos)}
                        </td>
                        {/* Pendiente */}
                        <td style={{ padding: "14px 12px", textAlign: "right", fontWeight: s.saldo > 0 ? 700 : 400,
                          color: s.saldo > 0 ? "#f59e0b" : "#cbd5e1" }}>
                          {fmt(s.saldo)}
                        </td>
                        {/* Cobranza % */}
                        <td style={{ padding: "14px 12px", textAlign: "center" }}>
                          <span style={{
                            background: s.cobPct >= 80 ? "#d1fae5" : s.cobPct >= 50 ? "#fef3c7" : "#fee2e2",
                            color: s.cobPct >= 80 ? "#065f46" : s.cobPct >= 50 ? "#78350f" : "#991b1b",
                            borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700,
                          }}>
                            {s.cobPct.toFixed(1)}%
                          </span>
                        </td>
                        {/* Progress % del total */}
                        <td style={{ padding: "14px 12px", minWidth: 150 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flexGrow: 1, height: 8, background: "#f1f5f9",
                              borderRadius: 10, overflow: "hidden" }}>
                              <div style={{
                                height: "100%",
                                width: `${Math.min(parseFloat(s.pctImp), 100)}%`,
                                background: `linear-gradient(90deg, ${cfg.color}, ${cfg.color}cc)`,
                                borderRadius: 10,
                                transition: "width 0.8s ease",
                              }} />
                            </div>
                            <span style={{ fontSize: "0.75rem", fontWeight: 800,
                              color: cfg.color, minWidth: 38, textAlign: "right" }}>
                              {s.pctImp}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "2px solid #e2e8f0", background: "#f8fafc" }}>
                    <td style={{ padding: "12px 12px" }}>
                      <span style={{ fontWeight: 700, color: "#475569", fontSize: "0.72rem",
                        textTransform: "uppercase", letterSpacing: "0.07em" }}>TOTAL</span>
                    </td>
                    <td style={{ padding: "12px 12px", textAlign: "right", fontWeight: 700, color: "#0f172a" }}>
                      {stats.total}
                    </td>
                    <td style={{ padding: "12px 12px", textAlign: "right", fontWeight: 800, color: "#0f172a" }}>
                      {fmt(stats.totalImporte)}
                    </td>
                    <td style={{ padding: "12px 12px", textAlign: "right", fontWeight: 700, color: "#10b981" }}>
                      {fmt(stats.totalCobrado)}
                    </td>
                    <td style={{ padding: "12px 12px", textAlign: "right", fontWeight: 700,
                      color: stats.totalSaldo > 0 ? "#f59e0b" : "#94a3b8" }}>
                      {fmt(stats.totalSaldo)}
                    </td>
                    <td style={{ padding: "12px 12px", textAlign: "center" }}>
                      <span style={{
                        background: cobPctGlobal >= 80 ? "#d1fae5" : cobPctGlobal >= 50 ? "#fef3c7" : "#fee2e2",
                        color: cobPctGlobal >= 80 ? "#065f46" : cobPctGlobal >= 50 ? "#78350f" : "#991b1b",
                        borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700,
                      }}>
                        {cobPctGlobal.toFixed(1)}%
                      </span>
                    </td>
                    <td style={{ padding: "12px 12px" }}>
                      <span style={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600 }}>100%</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* ── Gráfica Mensualidad (ancho completo) ─────────────────────── */}
            {monthlyData.length > 0 && (
              <>
                <div style={{
                  background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0",
                  padding: "20px 20px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", marginTop: 16,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: "#d1fae5", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <IcSync style={{ color: "#10b981" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "#0f172a" }}>Mensualidad por mes</div>
                      <div style={{ fontSize: "0.66rem", color: "#94a3b8" }}>Importe facturado mensualmente</div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={monthlyData} margin={{ top: 20, right: 16, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={52} />
                      <Tooltip
                        contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: "0.74rem" }}
                        formatter={(v) => [fmt(v), "Mensualidad"]}
                      />
                      <Bar dataKey="Mensualidad" fill="#10b981" radius={[5, 5, 0, 0]} maxBarSize={48}>
                        <LabelList
                          dataKey="Mensualidad"
                          position="top"
                          style={{ fontSize: "0.65rem", fontWeight: 700, fill: "#10b981" }}
                          formatter={(v) => (v > 0 ? fmtK(v) : "")}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* ── Gráfica Setup (ancho completo) ───────────────────────── */}
                <div style={{
                  background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0",
                  padding: "20px 20px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", marginTop: 16,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <IcCog style={{ color: "#3b82f6" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "#0f172a" }}>Setup por mes</div>
                      <div style={{ fontSize: "0.66rem", color: "#94a3b8" }}>Importe facturado mensualmente</div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={monthlyData} margin={{ top: 20, right: 16, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={52} />
                      <Tooltip
                        contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: "0.74rem" }}
                        formatter={(v) => [fmt(v), "Setup"]}
                      />
                      <Bar dataKey="Setup" fill="#3b82f6" radius={[5, 5, 0, 0]} maxBarSize={48}>
                        <LabelList
                          dataKey="Setup"
                          position="top"
                          style={{ fontSize: "0.65rem", fontWeight: 700, fill: "#3b82f6" }}
                          formatter={(v) => (v > 0 ? fmtK(v) : "")}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* ── Gráfica total apilada (ancho completo) ───────────────── */}
                <div style={{
                  background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0",
                  padding: "20px 20px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", marginTop: 16,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <IcChartBar style={{ color: "#475569" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "#0f172a" }}>Total por categoría y mes</div>
                      <div style={{ fontSize: "0.66rem", color: "#94a3b8" }}>Importe acumulado — todas las categorías</div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyData} margin={{ top: 16, right: 16, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={52} />
                      <Tooltip
                        contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: "0.74rem" }}
                        formatter={(v, name) => [fmt(v), name]}
                      />
                      <Legend wrapperStyle={{ fontSize: "0.72rem", paddingTop: 12 }} />
                      <Bar dataKey="Complemento" stackId="a" fill="#f59e0b" maxBarSize={52} />
                      <Bar dataKey="Otros"       stackId="a" fill="#94a3b8" maxBarSize={52} />
                      <Bar dataKey="Setup"       stackId="a" fill="#3b82f6" maxBarSize={52} />
                      <Bar dataKey="Mensualidad" stackId="a" fill="#10b981" maxBarSize={52} shape={(p) => <TopRoundedBar {...p} />}>
                        <LabelList
                          dataKey="total"
                          position="top"
                          style={{ fontSize: "0.65rem", fontWeight: 700, fill: "#475569" }}
                          formatter={(v) => (v > 0 ? fmtK(v) : "")}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default ReporteCargos;
